import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { VIDEOS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sparkles, RefreshCw, Trash2, Music2, Hash, Pencil, Upload, PlayCircle, HardDrive, Layers, Wand2 } from "lucide-react";
import BulkVideoUpload from "@/components/BulkVideoUpload";
import VideoIdeas from "@/components/VideoIdeas";

const NONE = "__none__";

const emptyForm = {
  filename: "",
  size_mb: 20,
  duration_sec: 30,
  thumbnail_url: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
  caption: "",
  hashtags: [],
  trending_sound: "",
  assigned_account_id: null,
  status: "draft",
};

export default function Videos() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [aiBusy, setAiBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // null = idle, 0..100 = uploading
  const [playing, setPlaying] = useState(null); // video doc currently in the player
  const [bulkOpen, setBulkOpen] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [v, a] = await Promise.all([api.videos(), api.accounts()]);
      setList(v); setAccounts(a);
    } catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const accountFor = (id) => accounts.find((a) => a.id === id);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const pickFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadPct(0);
    try {
      const created = await api.uploadVideo(file, {}, (p) => setUploadPct(p));
      toast.success(t.videos.uploadDone(created.filename));
      load();
    } catch {
      toast.error(t.videos.uploadFailed);
    } finally {
      setUploadPct(null);
      // reset input so uploading the same file twice re-triggers change
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({
      filename: v.filename || "",
      size_mb: v.size_mb ?? 0,
      duration_sec: v.duration_sec ?? 0,
      thumbnail_url: v.thumbnail_url || "",
      caption: v.caption || "",
      hashtags: v.hashtags || [],
      trending_sound: v.trending_sound || "",
      assigned_account_id: v.assigned_account_id || null,
      status: v.status || "draft",
    });
    setOpen(true);
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const acc = accountFor(form.assigned_account_id);
      const res = await api.aiCaption({
        niche: "crypto & day trading",
        geo: "US",
        persona_hint: acc?.username ? `like ${acc.username}` : undefined,
        theme: form.caption || "trading insight, hook first",
      });
      setForm((f) => ({
        ...f,
        caption: res.draft.caption || f.caption,
        hashtags: Array.isArray(res.draft.hashtags) ? res.draft.hashtags : f.hashtags,
        trending_sound: res.draft.suggested_sound || f.trending_sound,
      }));
      toast.success(t.videos.aiDone);
    } catch { toast.error(t.common.failed); }
    finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!form.filename) return toast.error("Имя файла обязательно");
    const payload = {
      ...form,
      size_mb: Number(form.size_mb),
      duration_sec: Number(form.duration_sec),
      assigned_account_id: form.assigned_account_id || null,
      hashtags: Array.isArray(form.hashtags) ? form.hashtags : (form.hashtags || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.updateVideo(editing, payload);
      else await api.createVideo(payload);
      toast.success(t.common.saved);
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try { await api.deleteVideo(id); load(); }
    catch { toast.error(t.common.failed); }
  };

  const queue = async (v) => {
    try {
      await api.updateVideo(v.id, { status: v.status === "queued" ? "draft" : "queued" });
      load();
    } catch { toast.error(t.common.failed); }
  };

  return (
    <div data-testid={VIDEOS.root}>
      <PageHeader
        title={t.videos.title}
        description={t.videos.description}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
              onChange={handleFile}
              className="hidden"
              data-testid={VIDEOS.uploadInput}
            />
            <button
              data-testid={VIDEOS.uploadTrigger}
              onClick={pickFile}
              disabled={uploadPct !== null}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-60 transition-colors"
            >
              {uploadPct !== null ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t.videos.uploading} {uploadPct}%
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> {t.videos.upload}
                </>
              )}
            </button>
            <button
              data-testid={VIDEOS.bulkTrigger}
              onClick={() => setBulkOpen(true)}
              disabled={uploadPct !== null}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-60 transition-colors"
            >
              <Layers className="h-4 w-4" /> {t.videos.bulk.trigger}
            </button>
            <button
              data-testid={VIDEOS.ideasTrigger}
              onClick={() => setIdeasOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-sm font-medium transition-colors"
            >
              <Wand2 className="h-4 w-4" /> {t.videos.ideas.trigger}
            </button>
            <button
              data-testid={VIDEOS.addBtn}
              onClick={openNew}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 text-slate-700 text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" /> {t.videos.add}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center text-slate-500 text-sm">
          {t.videos.empty}. {t.videos.emptyHint}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((v) => {
            const acc = accountFor(v.assigned_account_id);
            return (
              <div key={v.id} className="card-soft overflow-hidden">
                <div className="h-40 bg-slate-100 relative overflow-hidden">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <PlayCircle className="h-14 w-14" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3"><StatusBadge status={v.status} /></div>
                  {v.storage_key && (
                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] uppercase tracking-widest">
                      <HardDrive className="h-3 w-3" /> {t.videos.fileHere}
                    </div>
                  )}
                  {v.storage_key && (
                    <button
                      data-testid={VIDEOS.playBtn(v.id)}
                      onClick={() => setPlaying(v)}
                      className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors text-white opacity-0 hover:opacity-100"
                      aria-label={t.videos.play}
                    >
                      <PlayCircle className="h-14 w-14" />
                    </button>
                  )}
                  <div className="absolute top-3 right-3 flex gap-1">
                    <button
                      data-testid={VIDEOS.editBtn(v.id)}
                      onClick={() => openEdit(v)}
                      className="h-8 w-8 rounded-lg bg-white/90 hover:bg-emerald-500 hover:text-white text-slate-600 flex items-center justify-center transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={VIDEOS.deleteBtn(v.id)}
                      onClick={() => del(v.id)}
                      className="h-8 w-8 rounded-lg bg-white/90 hover:bg-rose-500 hover:text-white text-slate-600 flex items-center justify-center transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs font-mono text-slate-500 truncate">{v.filename}</div>
                  <p className="text-sm text-slate-800 mt-2 line-clamp-2">
                    {v.caption || <span className="text-slate-400">описания нет</span>}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(v.hashtags || []).slice(0, 5).map((h) => (
                      <span key={h} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        #{h}
                      </span>
                    ))}
                  </div>
                  {v.trending_sound && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                      <Music2 className="h-3.5 w-3.5" /> {v.trending_sound}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <div className="text-xs text-slate-500">
                      {acc ? acc.username : "не назначен"} · {v.duration_sec}с
                    </div>
                    <button
                      data-testid={VIDEOS.queueBtn(v.id)}
                      onClick={() => queue(v)}
                      className="text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-emerald-100 hover:text-emerald-700 text-slate-700 transition-colors"
                    >
                      {v.status === "queued" ? t.videos.unqueue : t.videos.queue}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.videos.edit : t.videos.add}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.filename}</Label>
              <Input value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })} placeholder="clip_v2.mp4" className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.sizeMb}</Label>
              <Input type="number" value={form.size_mb} onChange={(e) => setForm({ ...form, size_mb: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.duration}</Label>
              <Input type="number" value={form.duration_sec} onChange={(e) => setForm({ ...form, duration_sec: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.thumbnail}</Label>
              <Input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="mt-1 font-mono text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.account}</Label>
              <Select
                value={form.assigned_account_id || NONE}
                onValueChange={(v) => setForm({ ...form, assigned_account_id: v === NONE ? null : v })}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder={t.common.unlinked} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t.common.unlinked}</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.fields.caption}</Label>
                <button
                  data-testid={VIDEOS.aiCaptionBtn}
                  onClick={runAi}
                  disabled={aiBusy}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 transition-colors"
                >
                  {aiBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  {aiBusy ? "…" : t.videos.aiDraft}
                </button>
              </div>
              <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1"><Hash className="h-3 w-3" /> {t.videos.fields.hashtags}</Label>
              <Input
                value={Array.isArray(form.hashtags) ? form.hashtags.join(", ") : form.hashtags}
                onChange={(e) => setForm({ ...form, hashtags: e.target.value.split(",").map((s) => s.trim()) })}
                className="mt-1"
                placeholder="crypto, btc, fyp"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1"><Music2 className="h-3 w-3" /> {t.videos.fields.sound}</Label>
              <Input value={form.trending_sound} onChange={(e) => setForm({ ...form, trending_sound: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={VIDEOS.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!playing} onOpenChange={(o) => { if (!o) setPlaying(null); }}>
        <DialogContent className="rounded-2xl max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display truncate">
              {playing?.filename}
            </DialogTitle>
          </DialogHeader>
          {playing?.storage_key ? (
            <video
              data-testid={VIDEOS.player}
              key={playing.id}
              controls
              autoPlay
              className="w-full rounded-xl bg-black max-h-[70vh]"
              src={api.videoFileUrl(playing.id)}
            />
          ) : (
            <div className="p-10 text-center text-sm text-slate-500">{t.videos.noFile}</div>
          )}
          <DialogFooter>
            <button
              data-testid={VIDEOS.playerClose}
              onClick={() => setPlaying(null)}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
            >
              {t.common.close}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkVideoUpload
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        accounts={accounts}
        onFinished={load}
      />

      <VideoIdeas
        open={ideasOpen}
        onOpenChange={setIdeasOpen}
        accounts={accounts}
        onDraftCreated={load}
      />
    </div>
  );
}
